import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { XApi } from '../../services.js';

export function useDetailResource(key, load) {
  const active = useRef(key);
  active.current = key;
  const [state, setState] = useState(null);
  const [revision, retry] = useState(0);
  useEffect(() => {
    let cancelled = false;
    if (!load) { setState(null); return; }
    setState({ key, loading: true });
    Promise.resolve().then(load).then(data => {
      if (data == null) throw Error('数据暂不可用');
      if (!cancelled && active.current === key) setState({ key, data, loading: false });
    }).catch(error => {
      if (!cancelled && active.current === key) setState({ key, error, loading: false });
    });
    return () => { cancelled = true; };
  }, [key, load, revision]);
  return { ...(load && state?.key === key ? state : {}), retry: () => retry(n => n + 1) };
}

export function useDetailPanel(key, enabled, method, apiId, options, targetId) {
  const load = useCallback(() => targetId ? method(apiId, targetId, options) : method(apiId, options), [method, apiId, options, targetId]);
  return useDetailResource(key, enabled ? load : null);
}

/** Each route/account/window gets its own admission and response generation.
 * A failed append retains the exact cursor; old completions cannot unlock or
 * overwrite another window. No completed-result cache is introduced. */
export function useDetailLogs(apiId, bounds, filter) {
  const key = JSON.stringify([apiId, bounds, filter]);
  const context = useMemo(() => ({ key, sequence: 0, busy: false, mounted: false, accepted: new Set() }), [key]);
  const active = useRef(context); active.current = context;
  const [state, setState] = useState(null);
  const stateRef = useRef(state); stateRef.current = state;
  const [status, setStatus] = useState(null);
  const fetchLogs = useCallback(async ({ append = false, cursor = null } = {}) => {
    if (!apiId || !bounds || active.current !== context || !context.mounted || context.busy) return;
    if (append && (stateRef.current?.key !== key || stateRef.current.cursor !== cursor || !cursor || context.accepted.has(cursor))) return;
    context.busy = true;
    const sequence = ++context.sequence;
    const current = () => active.current === context && context.mounted && context.sequence === sequence;
    if (!append) context.accepted.clear();
    if (!append) setState({ key, items: [], cursor: null, hasMore: false });
    setStatus({ key, loading: true });
    try {
      const res = await XApi.providerDashboardService.apiLogs(apiId, { ...bounds, ...filter, scope: 'rest', limit: 50, cursor: cursor || undefined });
      if (!res) throw Error('日志暂不可用');
      if (!current()) return;
      if (append) context.accepted.add(cursor);
      setState(previous => ({ key, items: append ? [...(previous?.key === key ? previous.items : []), ...res.items] : res.items, cursor: res.nextCursor, hasMore: Boolean(res.nextCursor) }));
      setStatus({ key, loading: false });
    } catch (error) {
      if (current()) setStatus({ key, loading: false, error });
    } finally { if (current()) context.busy = false; }
  }, [apiId, key, context, bounds, filter]);
  useEffect(() => {
    context.mounted = true; context.busy = false;
    fetchLogs();
    return () => { context.mounted = false; context.sequence++; };
  }, [context, fetchLogs]);
  const data = state?.key === key ? state : null;
  return { logs: data?.items ?? [], logCursor: data?.cursor ?? null, logHasMore: data?.hasMore ?? false,
    logsLoading: status?.key === key && status.loading, logsError: status?.key === key ? status.error : null, fetchLogs };
}

// Only expected history contracts are shown verbatim; arbitrary transport errors
// retain the page's generic failure text.
export function historicalDetailError(error) {
  const code = error?.data?.code;
  if (!['HISTORICAL_DATA_UNAVAILABLE', 'HISTORICAL_HOUR_ALIGNMENT_REQUIRED', 'ENDPOINT_HISTORY_FACTS_UNAVAILABLE', 'HISTORICAL_TIMING_FACTS_UNAVAILABLE'].includes(code)) return null;
  return typeof error.data.message === 'string' ? error.data.message : null;
}
